import React, { useState, Suspense, lazy } from "react";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, CircularProgress } from "@mui/material";
import Layout from "./Layout";
import Login from "../pages/Login";
import { PrivateRoute } from "./PrivateRoute";
import Overview from "../assets/Overview.svg";
import RulePreviewPage from "./Rule/RulePreviewPage";

// Lazy load components
const Analytics = lazy(() => import("../pages/Analytics"));
const EditRules = lazy(() => import("./offers/EditRules"));
const EditOffer = lazy(() => import("./offers/EditOffer"));
const Limits = lazy(() => import("../pages/Limits"));
const PreviewOffer = lazy(() => import("../pages/PreviewOffer"));
const ForgotPassword = lazy(() => import("../pages/ForgotPassword"));
const NotFound = lazy(() => import("../pages/NotFound"));
const SetQualifyingCriteria = lazy(() =>
  import("./offers/Create-Offer/SetQualifyingCriteria")
);
const OfferDetailsPage = lazy(() =>
  import("./offers/Create-Offer/OfferDetailsPage")
);
const CreateGamePage = lazy(() =>
  import("./offers/Create-Offer/CreateGamePage")
);
const CreateRulePage = lazy(() => import("./Rule/CreateRulePage"));
const CreateCohortPage = lazy(() => import("./Cohort/CreateCohortPage"));
const PreviewPage = lazy(() => import("./offers/Create-Offer/PreviewPage"));
const ViewOfferPage = lazy(() => import("./offers/View-Offer/ViewOfferPage"));
const ViewRulePage = lazy(() => import("./Rule/view-rules/ViewRulesPage"));
const ViewCohortPage = lazy(() =>
  import("./Cohort/view-cohort/ViewCohortPage")
);
const TransactionsPage = lazy(() => import("./Transaction/TransactionsPage"));
const AnalyticsPage = lazy(() => import("./analytics/AnalyticsPage"));
const LimitsPage = lazy(() => import("./Limits/LimitsPage"));

// Loading component
const LoadingFallback = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="200px"
    flexDirection="column"
    gap={2}
    sx={{
      width: "100%",
      height: "calc(100vh - 200px)",
    }}
  >
    <CircularProgress />
    {/* <Box>Loading...</Box> */}
  </Box>
);

const schema = {
  rule: "",
  description: "",
  id: 1,
  type: "",
  whereSection: [{}],
};

const AllRoutes = () => {
  const [tabData, setTabData] = useState([schema]);
  const initialRoute = useSelector((store) => store.AuthReducer.initialRoute);
  const allowedData = useSelector((store) => store.AuthReducer.allowedRoutes);
  const userAllowedData = mapToNavItems(allowedData);
  const isAuth = useSelector((store) => store.AuthReducer.token);

  function mapToNavItems(routes) {
    const navItems = [];

    function mapRoute(route, icons = true) {
      const navItem = {
        text: route.title,
        disabled: route.disabled,
        icon: icons ? (
          <Box
            component="img"
            sx={{
              height: 25,
              width: 25,
              maxHeight: { xs: 200, md: 167 },
              maxWidth: { xs: 200, md: 250 },
            }}
            alt="The house from the offer."
            src={Overview}
          />
        ) : null,
        url: route?.path?.substring(1), // remove the leading '/'
      };
      navItems.push(navItem);
      if (route.subMenu) {
        route.subMenu?.forEach((subRoute) => mapRoute(subRoute, false));
      }
    }

    routes?.forEach((route) => mapRoute(route));

    return navItems;
  }

  const routesObj = {
    analytics: <AnalyticsPage />,
    "offers/create-offers/offer-details": <OfferDetailsPage />,
    "view-offers": <ViewOfferPage />,
    "create-rules": <CreateRulePage />,
    "view-rules": <ViewRulePage />,
    "view-cohorts": <ViewCohortPage />,
    "create-cohorts": <CreateCohortPage />,
    transactions: <TransactionsPage />,
    limits: <LimitsPage />,
  };

  const generatedRoutes = userAllowedData?.map((e, index) => {
    if (routesObj.hasOwnProperty(e.url)) {
      return (
        <Route
          key={index}
          path={`/${e.url}`}
          element={
            <PrivateRoute>
              <Suspense fallback={<LoadingFallback />}>
                {routesObj[e.url]}
              </Suspense>
            </PrivateRoute>
          }
        />
      );
    }
    return null;
  });

  console.log(generatedRoutes, "generatedRoutes");

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          path="/forgot-password"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <ForgotPassword />
            </Suspense>
          }
        />
        <Route element={<Layout />}>
          <Route
            path="/"
            element={
              <PrivateRoute>
                <Navigate
                  to={
                    initialRoute ? initialRoute : `/${userAllowedData[0]?.url}`
                  }
                  replace
                />
              </PrivateRoute>
            }
          />
          {generatedRoutes}
          <Route
            path="/preview-offer/:id"
            element={
              <PrivateRoute>
                <Suspense fallback={<LoadingFallback />}>
                  <PreviewOffer />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/edit-offers/:id"
            element={
              <PrivateRoute>
                <Suspense fallback={<LoadingFallback />}>
                  <EditOffer />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/*"
            element={
              isAuth ? (
                <Suspense fallback={<LoadingFallback />}>
                  <NotFound />
                </Suspense>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/offers/edit-offers/offer-details/:id"
            element={
              <PrivateRoute>
                <Suspense fallback={<LoadingFallback />}>
                  <OfferDetailsPage />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/offers/create-offers/set-qualifying-criteria"
            element={
              <PrivateRoute>
                <Suspense fallback={<LoadingFallback />}>
                  <SetQualifyingCriteria />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/offers/edit-offers/set-qualifying-criteria/:id"
            element={
              <PrivateRoute>
                <Suspense fallback={<LoadingFallback />}>
                  <SetQualifyingCriteria />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/offers/create-offers/set-qualifying-criteria/create-new-rule"
            element={
              <PrivateRoute>
                <Suspense fallback={<LoadingFallback />}>
                  <CreateRulePage />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/offers/edit-offers/set-qualifying-criteria/create-new-rule"
            element={
              <PrivateRoute>
                <Suspense fallback={<LoadingFallback />}>
                  <CreateRulePage />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/offers/create-offers/set-qualifying-criteria/create-new-cohort"
            element={
              <PrivateRoute>
                <Suspense fallback={<LoadingFallback />}>
                  <CreateCohortPage />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/offers/edit-offers/set-qualifying-criteria/create-new-cohort"
            element={
              <PrivateRoute>
                <Suspense fallback={<LoadingFallback />}>
                  <CreateCohortPage />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/offers/create-offers/create-game"
            element={
              <PrivateRoute>
                <Suspense fallback={<LoadingFallback />}>
                  <CreateGamePage />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/offers/edit-offers/create-game/:id"
            element={
              <PrivateRoute>
                <Suspense fallback={<LoadingFallback />}>
                  <CreateGamePage />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/offers/create-offers/preview"
            element={
              <PrivateRoute>
                <Suspense fallback={<LoadingFallback />}>
                  <PreviewPage />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/offers/approve-offers/preview"
            element={
              <PrivateRoute>
                <Suspense fallback={<LoadingFallback />}>
                  <PreviewPage />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/offers/edit-offers/preview/:id"
            element={
              <PrivateRoute>
                <Suspense fallback={<LoadingFallback />}>
                  <PreviewPage />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/preview-rule"
            element={
              <PrivateRoute>
                <Suspense fallback={<LoadingFallback />}>
                <RulePreviewPage />
                </Suspense>
              </PrivateRoute>
            }
          />
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

export default AllRoutes;

// old code without lazy loading

// import React, { useState } from "react";
// import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
// import Layout from "./Layout";
// import Analytics from "../pages/Analytics";
// import Login from "../pages/Login";
// import { PrivateRoute } from "./PrivateRoute";
// import EditRules from "./offers/EditRules";
// import EditOffer from "./offers/EditOffer";
// import Limits from "../pages/Limits";
// import PreviewOffer from "../pages/PreviewOffer";
// import ForgotPassword from "../pages/ForgotPassword";
// import { useSelector } from "react-redux";
// import Overview from "../assets/Overview.svg";
// import { Box } from "@mui/material";
// import NotFound from "../pages/NotFound";
// import SetQualifyingCriteria from "./offers/Create-Offer/SetQualifyingCriteria";
// import OfferDetailsPage from "./offers/Create-Offer/OfferDetailsPage";
// import CreateGamePage from "./offers/Create-Offer/CreateGamePage";
// import CreateRulePage from "./Rule/CreateRulePage";
// import CreateCohortPage from "./Cohort/CreateCohortPage";
// import PreviewPage from "./offers/Create-Offer/PreviewPage";
// import ViewOfferPage from "./offers/View-Offer/ViewOfferPage";
// import ViewRulePage from "./Rule/view-rules/ViewRulesPage";
// import ViewCohortPage from "./Cohort/view-cohort/ViewCohortPage";
// import TransactionsPage from "./Transaction/TransactionsPage";
// import AnalyticsPage from "./analytics/AnalyticsPage";
// import LimitsPage from "./Limits/LimitsPage";

// const schema = {
//   rule: "",
//   description: "",
//   id: 1,
//   type: "",
//   whereSection: [{}],
// };

// const AllRoutes = () => {
//   const [tabData, setTabData] = useState([schema]);
//   const initialRoute = useSelector((store) => store.AuthReducer.initialRoute);
//   const allowedData = useSelector((store) => store.AuthReducer.allowedRoutes);
//   const userAllowedData = mapToNavItems(allowedData);
//   const isAuth = useSelector(store=>store.AuthReducer.token);

//   function mapToNavItems(routes) {
//     const navItems = [];

//     function mapRoute(route, icons = true) {
//       const navItem = {
//         text: route.title,
//         disabled: route.disabled,
//         icon: icons ? (
//           <Box
//             component="img"
//             sx={{
//               height: 25,
//               width: 25,
//               maxHeight: { xs: 200, md: 167 },
//               maxWidth: { xs: 200, md: 250 },
//             }}
//             alt="The house from the offer."
//             src={Overview}
//           />
//         ) : null,
//         url: route?.path?.substring(1), // remove the leading '/'
//       };
//       navItems.push(navItem);
//       if (route.subMenu) {
//         route.subMenu?.forEach((subRoute) => mapRoute(subRoute, false));
//       }
//     }

//     routes?.forEach((route) => mapRoute(route));

//     return navItems;
//   }

//   const routesObj = {
//     analytics: <AnalyticsPage />,
//     // "create-offers": <OfferDetailsPage />,
//     "offers/create-offers/offer-details": <OfferDetailsPage />,
//     "view-offers": <ViewOfferPage />,
//     "create-rules": (
//       <CreateRulePage/>
//     ),
//     "view-rules": <ViewRulePage />,
//     "view-cohorts": <ViewCohortPage />,
//     "create-cohorts":   <CreateCohortPage />,
//     transactions: <TransactionsPage />,
//     limits: <LimitsPage />,
//   };

//   const generatedRoutes = userAllowedData?.map((e, index) => {
//     if (routesObj.hasOwnProperty(e.url)) {
//       return (
//         <Route
//           key={index}
//           path={`/${e.url}`}
//           element={<PrivateRoute>{routesObj[e.url]}</PrivateRoute>}
//         />
//       );
//     }
//     return null;
//   });
// console.log(generatedRoutes, "generatedRoutes");
//   return (
//     <BrowserRouter>
//       <Routes>
//         <Route path="/login" element={<Login />} />
//         <Route path="/forgot-password" element={<ForgotPassword />} />
//         <Route element={<Layout />}>
//           {/* <Route path="/" element={<Navigate to="/view-rules" replace />} /> */}
//           <Route
//             path="/"
//             element={
//               <PrivateRoute>
//                 <Navigate to={initialRoute?initialRoute:`/${userAllowedData[0]?.url}`} replace />
//                 {/* <Navigate to={initialRoute} replace /> */}
//               </PrivateRoute>
//             }
//           />
//           {generatedRoutes}
//           <Route
//             path="/preview-offer/:id"
//             element={
//               <PrivateRoute>
//                 <PreviewOffer />
//               </PrivateRoute>
//             }
//           />
//           <Route
//             path="/edit-offers/:id"
//             element={
//               <PrivateRoute>
//                 <EditOffer />
//               </PrivateRoute>
//             }
//           />
//           <Route
//             path="/preview-rule"
//             element={
//               <PrivateRoute>
//              <RulePreviewPage />
//               </PrivateRoute>
//             }
//           />
//           <Route path="/*" element={isAuth ? <NotFound /> : <Navigate to="/login" replace />} />
//           {/* <Route
//             path="/offers/create-offers/offer-details"
//             element={
//               <PrivateRoute>
//                 <OfferDetailsPage />
//               </PrivateRoute>
//             }
//           /> */}
//               <Route
//             path="/offers/edit-offers/offer-details/:id"
//             element={
//               <PrivateRoute>
//                 <OfferDetailsPage />
//               </PrivateRoute>
//             }
//           />
//           <Route
//             path="/offers/create-offers/set-qualifying-criteria"
//             element={
//               <PrivateRoute>
//                 <SetQualifyingCriteria />
//               </PrivateRoute>
//             }
//           />
//           <Route
//             path="/offers/edit-offers/set-qualifying-criteria/:id"
//             element={
//               <PrivateRoute>
//                 <SetQualifyingCriteria />
//               </PrivateRoute>
//             }
//           />
//           <Route
//             path="/offers/create-offers/set-qualifying-criteria/create-new-rule"
//             element={
//               <PrivateRoute>
//                 <CreateRulePage />
//               </PrivateRoute>
//             }
//           />
//           <Route
//             path="/offers/edit-offers/set-qualifying-criteria/create-new-rule"
//             element={
//               <PrivateRoute>
//                 <CreateRulePage />
//               </PrivateRoute>
//             }
//           />
//           <Route
//             path="/offers/create-offers/set-qualifying-criteria/create-new-cohort"
//             element={
//               <PrivateRoute>
//                 <CreateCohortPage />
//               </PrivateRoute>
//             }
//           />
//           <Route
//             path="/offers/edit-offers/set-qualifying-criteria/create-new-cohort"
//             element={
//               <PrivateRoute>
//                 <CreateCohortPage />
//               </PrivateRoute>
//             }
//           />
//           <Route
//             path="/offers/create-offers/create-game"
//             element={
//               <PrivateRoute>
//                 <CreateGamePage />
//               </PrivateRoute>
//             }
//           />
//           <Route
//             path="/offers/edit-offers/create-game/:id"
//             element={
//               <PrivateRoute>
//                 <CreateGamePage />
//               </PrivateRoute>
//             }
//           />
//           <Route
//             path="/offers/create-offers/preview"
//             element={
//               <PrivateRoute>
//                 <PreviewPage />
//               </PrivateRoute>
//             }
//           />
//           <Route
//             path="/offers/approve-offers/preview"
//             element={
//               <PrivateRoute>
//                 <PreviewPage />
//               </PrivateRoute>
//             }
//           />
//           <Route
//             path="/offers/edit-offers/preview/:id"
//             element={
//               <PrivateRoute>
//                 <PreviewPage />
//               </PrivateRoute>
//             }
//           />
//         </Route>
//       </Routes>
//     </BrowserRouter>
//   );
// };

// export default AllRoutes;
